import { InviteAcceptanceModel, PersonalInvite } from '../types/invites';
import { PersonalConnection, SharedBusinessData } from '../types/misc';
import { ProfileField } from '../types/profile';
import { UseLocalContactInviteRequest, UsePersonalInviteRequest } from '../types/requests';
import { DashboardMemberSharedDataResponse, PicturePathResponse, SharedDataResponse } from '../types/responses';
import httpService from './httpService';

export function createConnection(linkId: string, dataProvidedIds: string[]): Promise<string> {
  const body = {
    propertyIds: dataProvidedIds.map((propertyId) => +propertyId),
  };
  return httpService.post<string>(`/invitation-links/${linkId}/use`, body).then(({ data }): string => data);
}

export function createBusinessConnection(inviteId: string, args: SharedBusinessData): Promise<string> {
  const isNumeric = /^\d+$/.test(inviteId);
  if (isNumeric) {
    const body = {
      accept: true,
      sharedProperties: args.propertyIds.map((p) => +p),
    };
    return httpService.post<string>(`/networks/invites/${inviteId}/accept`, body).then(({ data }): string => data);
  }

  const body = {
    sharedPropertiesWithDashboard: args.propertyIds.map((p) => +p),
    sharedPropertiesWithMembers: args.dataForMembers?.map((p) => +p) || [],
    customFieldResponses: args.customFieldResponses || {},
  };
  return httpService.post<string>(`/networks/invite-links/${inviteId}/use`, body).then(({ data }): string => data);
}

export function createCommunityConnection(inviteId: string, args: SharedBusinessData): Promise<string> {
  const body = {
    accept: true,
    sharedProperties: args.propertyIds.map((p) => +p),
  };
  return httpService.post<string>(`/networks/invites/${inviteId}/accept`, body).then(({ data }): string => data);
}

// Moments connection flow removed in new API
/**
 * @deprecated Moments flow is removed. Use createCommunityConnection/createBusinessConnection.
 */
export function createMomentConnection(
  _inviteId: string,
  _propertyIds: string[],
  _communicationName: string | undefined = undefined,
): Promise<string> {
  JSON.stringify({ _inviteId, _propertyIds, _communicationName });
  console.warn('[deprecated] createMomentConnection is removed. Use createCommunityConnection instead.');
  return Promise.reject(new Error('Moments flow removed. Use createCommunityConnection.'));
}

export function getSharedDataWithUser(userId: string): Promise<ProfileField[]> {
  return httpService
    .get<SharedDataResponse>(`/connections/@me/${userId}/shared`)
    .then(({ data }): ProfileField[] => data.sharedDataWithTarget);
}

export function getSharedDataByUser(userId: string): Promise<ProfileField[]> {
  return httpService
    .get<SharedDataResponse>(`/connections/@me/${userId}/shared`)
    .then(({ data }): ProfileField[] => data.sharedDataByTarget);
}

export function getProfilePictureOfUser(userId: string): Promise<string> {
  return httpService.get<PicturePathResponse>(`/users/${userId}/avatar`).then(({ data }): string => data.path);
}

export function getSharedDataWithCommunity(communityId: number): Promise<DashboardMemberSharedDataResponse> {
  return httpService
    .get<DashboardMemberSharedDataResponse>(`/networks/communities/${communityId}/members/@me/shared-data`)
    .then(({ data }) => data);
}

export function getSharedDataWithBusiness(businessId: number): Promise<DashboardMemberSharedDataResponse> {
  return httpService
    .get<DashboardMemberSharedDataResponse>(`/networks/businesses/${businessId}/members/@me/shared-data`)
    .then(({ data }) => data);
}

export function updateConnection(targetId: string, dataIds: string[]): Promise<void> {
  const body = {
    propertyIds: dataIds.map((id) => +id),
  };
  return httpService.put<void>(`/connections/@me/${targetId}/shared`, body).then();
}

export function createLocalContactConnection(token: string, args: SharedBusinessData): Promise<string> {
  console.warn(
    '[deprecated] createLocalContactConnection is replaced by acceptExternalMemberInvite(dashboardId, token, body).',
  );
  const body: UseLocalContactInviteRequest = {
    ...args,
    propertyIds: args.propertyIds.map((f) => +f),
  };

  return httpService
    .post<string>(`/business/local-contact/invite/accept/${token}`, body)
    .then(({ data }): string => data);
}

export function acceptExternalMemberInvite(
  dashboardId: number,
  token: string,
  body: InviteAcceptanceModel,
): Promise<void> {
  return httpService
    .post(`/networks/dashboards/${dashboardId}/external-members/invites/use?token=${encodeURIComponent(token)}`, body)
    .then(() => {});
}

/**
 * @deprecated Moved to crmService.acceptCrmUserInvite(businessId, token, body)
 */
export function createCrmUserConnection(_token: string, _args: SharedBusinessData): Promise<void> {
  JSON.stringify({ _token, _args });
  console.warn('[deprecated] Use crmService.acceptCrmUserInvite(businessId, token, body)');
  return Promise.reject(new Error('Use crmService.acceptCrmUserInvite'));
}

export function createPersonalConnection(token: string, sharedProperties: number[]): Promise<PersonalInvite> {
  const body: UsePersonalInviteRequest = {
    accept: true,
    sharedProperties,
  };

  return httpService
    .post<PersonalInvite>(`/personal-invites/${token}/use`, body)
    .then(({ data }): PersonalInvite => data);
}

export function createOrUpdateDataRequest(otherId: string, dataTypesRequested: string[]): Promise<void> {
  const body = {
    dataTypesRequested,
  };
  return httpService.post(`/data-requests/users/${otherId}`, body).then((): void => {});
}

export function acceptDataRequest(
  id: { token?: string; dataRequestId?: number },
  dataProvidedIds: string[],
): Promise<void> {
  const { token, dataRequestId } = id;
  const params = [] as string[];
  if (typeof dataRequestId === 'number') params.push(`dataRequestId=${dataRequestId}`);
  if (token) params.push(`token=${encodeURIComponent(token)}`);
  const body = {
    propertyIds: dataProvidedIds.map((propertyId) => +propertyId),
  };
  return httpService
    .post(`/data-requests/accept${params.length ? `?${params.join('&')}` : ''}`, body)
    .then((): void => {});
}

export function declineDataRequest(dataRequestId: number): Promise<void> {
  return httpService.delete(`/data-requests/${dataRequestId}`).then((): void => {});
}

/**
 * @deprecated Removed in new API. No replacement.
 */
export function respondToCommunityContactInvite(
  _token: string,
  _communityId: number,
  _accepted: boolean,
): Promise<void> {
  JSON.stringify({ _token, _communityId, _accepted });
  console.warn('[deprecated] respondToCommunityContactInvite is removed in the new API.');
  return Promise.reject(new Error('respondToCommunityContactInvite removed in new API.'));
}

export function declineIntroduction(introductionId: string): Promise<void> {
  return httpService.post(`/introductions/${introductionId}/decline`, {}).then((): void => {});
}

export function acceptIntroduction(introductionId: string, dataProvidedIds: string[]): Promise<void> {
  const body = {
    propertyIds: dataProvidedIds.map((id) => +id),
  };
  return httpService.post(`/introductions/${introductionId}/accept`, body).then((): void => {});
}

export function acceptCommunityRequest(userId: number, communityId: number, sharedProps: number[]): Promise<void> {
  const body = {
    propertyIds: sharedProps,
  };
  return httpService.post(`/networks/communities/${communityId}/members/${userId}/connect`, body).then((): void => {});
}

export function declineCommunityRequest(userId: number): Promise<void> {
  return httpService.delete(`/networks/communities/connection-requests/others/${userId}`).then(() => {});
}

/**
 * Respond to a connection invite and create a connection between
 * the user if the invite is accepted
 *
 * @param inviteId the id of the invite to respond to
 * @param accept whether we accept the invite or not
 * @param sharedProps the list of shared properties if the invite is accepted
 */
export function respondToConnectionInvite(inviteId: number, accept: boolean, sharedProps?: string[]) {
  const body = {
    accept,
    sharedProperties: sharedProps ? sharedProps.map((p) => +p) : undefined,
  };
  return httpService.post(`/connection-invites/${inviteId}/respond`, body).then((): void => {});
}

export function updateDataSharedWithCommunityMembers(
  communityId: number,
  options: { replaceExisting: boolean; updateForExistingConnections: boolean; propertyIds: number[] },
): Promise<void>;
/**
 * @deprecated Old signature will be removed. Use (communityId, { replaceExisting, updateForExistingConnections }).
 */
export function updateDataSharedWithCommunityMembers(businessId: number, dataForMembers: number[]): Promise<void>;
export function updateDataSharedWithCommunityMembers(
  communityId: number,
  second: { replaceExisting: boolean; updateForExistingConnections: boolean; propertyIds: number[] } | number[],
): Promise<void> {
  if (Array.isArray(second)) {
    console.warn(
      '[deprecated] updateDataSharedWithCommunityMembers(businessId, dataForMembers) → use flags options instead.',
    );
    const options = { replaceExisting: true, updateForExistingConnections: true, propertyIds: second };
    return httpService
      .post(`/networks/communities/${communityId}/members/@me/shared-data-with-members`, options)
      .then((): void => {});
  }
  return httpService
    .post(`/networks/communities/${communityId}/members/@me/shared-data-with-members`, second)
    .then((): void => {});
}

/**
 * Get all personal connections for the current user
 */
export function getMyConnections(): Promise<PersonalConnection[]> {
  return httpService.get<PersonalConnection[]>('/connections/@me/overview').then(({ data }): PersonalConnection[] => {
    // Normalize API shape where `userId` may be provided instead of `id`.
    const mapped = (data as any[]).map((c) => {
      const id = c.id ?? c.userId;
      return { ...c, id };
    }) as PersonalConnection[];
    return mapped;
  });
}

/**
 * Delete a connection with another user
 */
export function deleteConnection(otherId: number): Promise<void> {
  return httpService.delete(`/connections/@me/${otherId}`).then((): void => {});
}
